import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useState } from 'react';
import {
  Link
} from "react-router-dom";
import styled from 'styled-components';
import { BatteryIndicator } from '../../components/BatteryIndicator';
import {
  BiggestText,
  Box,
  ClickToCopy,
  DeviceICCIDLink,
  DevicePositionDescription,
  FieldDescription,
  SectionHeaderText,
  StatusIndicator,
  Text,
  capitalizeFirstLetter,
  formatAsDate,
  formatAsDateAndTime
} from '../../components/Common';
import { DeviceChargingIndicator } from '../../components/DeviceChargingIndicator';
import { DeviceConfigSyncStateIndicator } from '../../components/DeviceConfigSyncStateIndicator';
import { DeviceSignalIndicator } from '../../components/DeviceSignalIndicator';
import { DeviceBatteryModeTag } from '../../components/device/DeviceBatteryModeTag';
import { DeviceImage } from '../../components/device/DeviceImage';
import { DeviceStatusTag } from '../../components/device/DeviceStatusTag';
import { useDeviceOdometer } from '../../hooks/useDeviceOdometer';
import { Device, deviceBatteryMode } from '../../models/Device';
import { MOBILE_MEDIA_QUERY } from '../../styles/breakpoints';

const AlignedSpan = styled.span`
  display: flex;
  gap: 4px;
`;

// A tappable "Show device details" bar. Hidden on desktop, where the full
// sidebar is always visible; only shown on mobile to collapse the long
// property list so the tabs/content sit near the top of the page.
const DetailsToggle = styled.button`
  display: none;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  /* width + side margins keep the card inset to 10px, matching the 10px
     padding of the Box content above and below it (aligned, symmetric). */
  width: calc(100% - 20px);
  background: #ffffff;
  border: #d8d8d8 1px solid;
  border-radius: 8px;
  padding: 12px 14px;
  margin: 0 10px 20px;
  font-family: inherit;
  font-size: 12pt;
  font-weight: 600;
  color: #414552;
  cursor: pointer;

  &:hover {
    background: #f7f7f7;
  }

  @media ${MOBILE_MEDIA_QUERY} {
    display: flex;
  }
`;

const Chevron = styled(KeyboardArrowDownRoundedIcon)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
`;

const DeviceDataQuota = ({ device }: { device: Device }) => {
  const { dataQuotaBytesUsed, dataQuotaBytesTotal } = device;

  const mbUsed = dataQuotaBytesUsed ? (dataQuotaBytesUsed / 1024 / 1024).toFixed(1) : '0';
  const mbTotal = dataQuotaBytesTotal ? (dataQuotaBytesTotal / 1024 / 1024).toFixed(0) : '--';

  return (<span>{mbUsed} / {mbTotal} MB</span>);
}

const DeviceSMSQuota = ({ device }: { device: Device }) => {
  const { smsQuotaUsed, smsQuotaTotal } = device;

  const used = smsQuotaUsed ? smsQuotaUsed : '0';
  const total = smsQuotaTotal ? smsQuotaTotal : '--';

  return (<span>{used} / {total}</span>);
}

const toMb = (bytes: number | undefined) => {
  if (!bytes) {
    return '0 MB';
  }

  const valueMb = (bytes / 1024 / 1024).toFixed(0);
  return `${valueMb} MB`;
}

const deviceBattery = (device?: Device | null): number | null => {
  return device?.battery || device?.lastPosition?.battery || null;
}

/**
 * Renders an info panel that describes all key properties of a given Device.
 */
export const DeviceProperties = ({ device }: { device: Device | undefined | null }) => {
  const activeSubscription = device && device.deviceSubscriptions.find(sub => sub.state === "active");

  const { data: deviceOdometer, isLoading: isDeviceOdometerLoading } = useDeviceOdometer(device?.imei || '');

  const batteryMode = deviceBatteryMode(device);

  // On mobile the details are collapsed by default so the tabs/content are
  // reachable without a long scroll. On desktop the full sidebar always shows.
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div>
      <Box>
        <BiggestText>{device?.name}</BiggestText>
        <ClickToCopy copyContent={device?.imei || ''} >
          <Text>{device?.imei}</Text>
        </ClickToCopy>
        { device ? (<DeviceStatusTag device={device} />) : null}
      </Box>

      <DetailsToggle
        type="button"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((open) => !open)}
      >
        {detailsOpen ? 'Hide device details' : 'Show device details'}
        <Chevron $open={detailsOpen} />
      </DetailsToggle>

      <Collapse in={!isMobile || detailsOpen}>
      <Box>
        <DeviceImage deviceModel={device?.model || 'unknown'} size={250} />
      </Box>

      <Box>
        <SectionHeaderText>Details</SectionHeaderText>
        <FieldDescription name="Owner">
          <ClickToCopy copyContent={device?.owner?.email || ''} >
           <Link to={`/users/${device?.owner?.id}`}>{device?.owner?.email}</Link>
          </ClickToCopy>
        </FieldDescription>
        <FieldDescription name="Make" value={`${device?.make}`} />
        <FieldDescription name="Model" value={`${device?.model}`} />
        <FieldDescription name="Firmware Version" value={device?.firmwareVersion} />
        <FieldDescription name="Firmware Updated At" value={formatAsDateAndTime(device?.firmwareUpdatedAt)} />
        { deviceBattery(device) != null ? (
          <FieldDescription name="Battery">
            <AlignedSpan>
              <BatteryIndicator percent={deviceBattery(device) || -1} />
              { device?.model === 'AT1' && <DeviceChargingIndicator charging={device?.charging || false} /> }
              { batteryMode && <DeviceBatteryModeTag batteryMode={batteryMode} /> }
            </AlignedSpan>
          </FieldDescription>
          ) : null}
        <FieldDescription name="Signal Strength">
        { (device?.model === 'AT1' && device.signal) ? (
          <DeviceSignalIndicator signal={device.signal || false} withDescription />
        ) : (
          "N/A"
        )}
        </FieldDescription>
        <FieldDescription name="Config Sync Status">
          <AlignedSpan>
            <DeviceConfigSyncStateIndicator syncState={device?.deviceConfigSyncState} />
            {capitalizeFirstLetter(device?.deviceConfigSyncState) || 'N/A'}
          </AlignedSpan>
        </FieldDescription>
        <FieldDescription name="Platform">
          {device?.owner?.platform === 'tracksolid' ? (
            <StatusIndicator label="Tracksolid" isSuccess={device.tracksolidSynced} />
          ) : (
            <span>
              {capitalizeFirstLetter(device?.owner?.platform )}
            </span>
          )}
        </FieldDescription>
        <FieldDescription name="Last Location">
          { device && <DevicePositionDescription device={device} /> }
        </FieldDescription>
        <FieldDescription name="Mileage (miles)">
          {isDeviceOdometerLoading ? <CircularProgress size={14} /> : (
            <span>{deviceOdometer?.mileageMiles.toFixed(2)}</span>
          )}
        </FieldDescription>
        <FieldDescription name="Active Since" value={formatAsDate(activeSubscription?.createdAt)} />
        <FieldDescription name="Last Seen" relativeTime={device?.lastseen ?? null} />
        <FieldDescription name="Last Positioned" relativeTime={device?.lastPosition?.position_at ?? null} />
        <FieldDescription name="SIM Provider" value={device?.simProvider} />
        <FieldDescription name="ICCID" value={device?.iccid}>
          <DeviceICCIDLink device={device} />
        </FieldDescription>
        <FieldDescription name="SIM Status" value={device?.simStatus || 'Unknown'} />
        <FieldDescription name="Data Quota">
          <DeviceDataQuota device={device!} />
        </FieldDescription>
        <FieldDescription name="SMS Quota">
          <DeviceSMSQuota device={device!} />
        </FieldDescription>
        <FieldDescription name="SIM Expires At">
          <StatusIndicator label={formatAsDate(device?.simExpiresAt) || 'N/A'} isSuccess={device?.simExpiresAt  ? new Date(device?.simExpiresAt) > new Date() : true} />
        </FieldDescription>
        <FieldDescription name="SIM Last Refreshed At" relativeTime={device?.simStatusLastRefreshedAt ?? null} />
        <FieldDescription name="Total Storage" value={toMb(device?.storageUsedBytes)} />
      </Box>
      </Collapse>
    </div>
  );
}
